import { useEffect, useRef, useState } from "react";
import type { LauncherClient } from "./launcherClient";

export type FactoryReadiness =
  | "idle"
  | "loading"
  | "verified"
  | "missingBytecode"
  | "mismatch"
  | "paused"
  | "rpcError";

export interface FactoryReadinessState {
  readiness: FactoryReadiness;
  /** True only when startup verification fully passed. */
  ready: boolean;
  /** Human blocking reason, or null when ready/idle. */
  reason: string | null;
}

const REASON: Record<Exclude<FactoryReadiness, "idle" | "loading" | "verified">, string> = {
  missingBytecode: "No factory bytecode found at the configured address.",
  mismatch: "The factory runtime bytecode does not match the expected pinned hash.",
  paused: "The factory is paused. Token creation is disabled.",
  rpcError: "Couldn't verify the factory on-chain (RPC error). Try again.",
};

/**
 * Startup gate. When `active` (a live factory is configured), this reads the factory
 * bytecode and paused state once on load and whenever the client changes, and
 * classifies the result. Review/Deploy and admin writes must stay disabled unless
 * `ready` is true. Stale results are discarded when the client changes. This
 * COMPLEMENTS the per-transaction rechecks in createToken/setFeeExempt — it does not
 * replace them.
 */
export function useFactoryReadiness(client: LauncherClient, active: boolean): FactoryReadinessState {
  const [readiness, setReadiness] = useState<FactoryReadiness>(active ? "loading" : "idle");
  const reqId = useRef(0);

  useEffect(() => {
    if (!active) {
      reqId.current += 1; // invalidate any in-flight request so it can't overwrite idle
      setReadiness("idle");
      return;
    }
    const id = ++reqId.current;
    setReadiness("loading");
    void (async () => {
      let next: FactoryReadiness;
      try {
        const s = await client.getFactoryStatus();
        if (!s.observedBytecodeHash) next = "missingBytecode";
        else if (!s.bytecodeVerified) next = "mismatch";
        else if (s.paused) next = "paused";
        else next = "verified";
      } catch {
        next = "rpcError";
      }
      if (id === reqId.current) setReadiness(next); // discard stale
    })();
  }, [client, active]);

  const ready = readiness === "verified";
  const reason =
    readiness === "idle" || readiness === "loading" || readiness === "verified"
      ? null
      : REASON[readiness];
  return { readiness, ready, reason };
}
